import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RootState } from '../store';
import {
  setSources,
  updateSourceStatus,
  setSyncInProgress,
  addDataQualityAlert,
} from '../store/dataSourcesSlice';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks, Breakpoints } from '../constants/theme';
import { DataSource } from '../types';
import { InteractiveButton } from '../components/MicroInteractions';
import { AccessibilityUtils } from '../utils/accessibility';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import { InlineSyncStatus } from '../components/RealTimeSyncIndicator';
import NotificationSystem from '../components/NotificationSystem';
import { useNotifications } from '../hooks/useNotifications';

// Ultra-Modern Professional Canvas
const ScreenContainer = styled.div`
  background: ${Colors.background};
  min-height: 100vh;
  position: relative;
  padding: 0;
  overflow-x: hidden;
  font-family: ${FontStacks.primary};
  
  /* Professional container layout */
  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: ${Spacing.xxxl}px ${Spacing.xl}px;
    /* Minimal top padding to prevent header overlap */
    padding-top: ${Spacing.xl}px; /* Just enough spacing to prevent overlap */
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${Spacing.xxxxl}px;
  padding-bottom: ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
  
  @media (max-width: ${Breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${Spacing.lg}px;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.display}px;
  font-weight: ${FontWeights.semibold};
  margin: 0;
  color: ${Colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.1;
  
  /* Ultra-clean underline */
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${Colors.secondary};
    border-radius: 1px;
  }
`;

const Subtitle = styled.p`
  font-size: ${FontSizes.lg}px;
  color: ${Colors.textSecondary};
  line-height: 1.5;
  margin: ${Spacing.sm}px 0 0 0;
  font-weight: ${FontWeights.normal};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  align-items: center;
`;

// System Status Section (moved to top)
const SystemStatusSection = styled.section`
  margin-top: calc(${Spacing.xxxxl}px + ${Spacing.xxxl}px); /* Add generous top margin to push below sticky header */
  margin-bottom: ${Spacing.xxxxl}px;
  position: relative;
  z-index: 1; /* Ensure it's above background elements */
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${Spacing.xxxl}px;
  padding-top: ${Spacing.xxl}px; /* Add generous top padding to ensure visibility below sticky header */
  padding-bottom: ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
  position: relative;
  z-index: 2; /* Ensure section headers are visible */
`;

const SectionTitle = styled.h2`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.display}px;
  font-weight: ${FontWeights.semibold};
  margin: 0;
  color: ${Colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.1;
  
  /* Ultra-clean underline */
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${Colors.secondary};
    border-radius: 1px;
  }
`;

const SystemStatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xxxxl}px;
  position: relative;
  z-index: 2; /* Ensure grid is visible above any background */
  padding-top: ${Spacing.md}px; /* Add some top padding for breathing room */

  @media (max-width: ${Breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacing.lg}px;
  }

  @media (max-width: ${Breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatusCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  text-align: center;
  transition: all 200ms ease;
  position: relative;
  z-index: 2; /* Ensure cards are visible above any background elements */
  min-height: 120px; /* Ensure minimum height for proper visibility */
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
    transform: translateY(-2px);
    z-index: 3; /* Higher z-index on hover */
  }
`;

const StatusValue = styled.div<{ color?: string }>`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  color: ${props => props.color || Colors.textPrimary};
  margin-bottom: ${Spacing.sm}px;
  line-height: 0.9;
  letter-spacing: -0.02em;
`;

const StatusLabel = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AlertsContainer = styled.div`
  margin-bottom: ${Spacing.xxxl}px;
`;

const AlertsTitle = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.xl}px 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const AlertCard = styled.div<{ severity: string }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-left: 4px solid ${props => 
    props.severity === 'HIGH' ? Colors.danger : Colors.warning
  };
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xl}px;
  margin-bottom: ${Spacing.lg}px;
  transition: all 200ms ease;
  cursor: pointer;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${props => 
      props.severity === 'HIGH' ? Colors.danger : Colors.warning
    };
    transform: translateY(-2px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AlertHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.md}px;
`;

const SeverityBadge = styled.span<{ severity: string }>`
  background: ${props => 
    props.severity === 'HIGH' ? Colors.danger + '20' : Colors.warning + '20'
  };
  color: ${props => 
    props.severity === 'HIGH' ? Colors.danger : Colors.warning
  };
  padding: ${Spacing.xs}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AlertTime = styled.span`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  font-weight: ${FontWeights.medium};
`;

const AlertMessage = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

const SourcesContainer = styled.div`
  display: grid;
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xxxxl}px;
`;

const SourceCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  transition: all 200ms ease;
  cursor: pointer;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
    transform: translateY(-2px);
  }
`;

const SourceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.xl}px;

  @media (max-width: ${Breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${Spacing.md}px;
  }
`;

const SourceInfo = styled.div`
  flex: 1;
`;

const SourceName = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.sm}px 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const SourceType = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textTertiary};
  margin: 0;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  background: ${props => {
    switch (props.status) {
      case 'CONNECTED': return Colors.success + '20';
      case 'SYNCING': return Colors.info + '20';
      case 'ERROR': return Colors.danger + '20';
      case 'DISCONNECTED': return Colors.textTertiary + '20';
      default: return Colors.textSecondary + '20';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'CONNECTED': return Colors.success;
      case 'SYNCING': return Colors.info;
      case 'ERROR': return Colors.danger;
      case 'DISCONNECTED': return Colors.textTertiary;
      default: return Colors.textSecondary;
    }
  }};
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  gap: ${Spacing.xs}px;
`;

const StatusIcon = styled.span`
  font-size: ${FontSizes.md}px;
`;

const SourceMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
  padding: ${Spacing.lg}px 0;
  border-top: 1px solid ${Colors.borderLight};
  border-bottom: 1px solid ${Colors.borderLight};
`;

const MetricItem = styled.div``;

const MetricLabel = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-bottom: ${Spacing.sm}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${FontWeights.medium};
`;

const MetricValue = styled.div<{ color?: string }>`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${props => props.color || Colors.textPrimary};
  line-height: 1.2;
`;

const QualityBar = styled.div`
  height: 8px;
  background-color: ${Colors.borderLight};
  border-radius: ${BorderRadius.sm}px;
  overflow: hidden;
  margin-bottom: ${Spacing.xl}px;
`;

const QualityProgress = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${props => props.width}%;
  background: linear-gradient(90deg, ${props => props.color}, ${Colors.secondary});
  border-radius: ${BorderRadius.sm}px;
  transition: width 0.6s ease;
`;

const SourceActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;

  @media (max-width: ${Breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{ disabled?: boolean; variant?: 'primary' | 'secondary' }>`
  flex: 1;
  background: ${props => props.variant === 'primary' ? Colors.secondary : 'transparent'};
  color: ${props => props.variant === 'primary' ? Colors.textOnPrimary : Colors.secondary};
  border: 1px solid ${props => props.variant === 'primary' ? Colors.secondary : Colors.border};
  padding: ${Spacing.md}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs}px;

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'primary' ? Colors.accent : Colors.surfaceHover};
    border-color: ${props => props.variant === 'primary' ? Colors.accent : Colors.borderDark};
    color: ${props => props.variant === 'primary' ? Colors.textOnPrimary : Colors.textPrimary};
    transform: translateY(-1px);
    box-shadow: ${Shadows.sm};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${Colors.secondary}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

// Modal Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 300ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xl}px;
`;

const ModalContainer = styled.div<{ isOpen: boolean }>`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.modal};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)'};
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${Colors.border};
`;

const ModalHeader = styled.div`
  padding: ${Spacing.xxxl}px ${Spacing.xxxl}px ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
  background: ${Colors.backgroundSecondary};
`;

const ModalTitle = styled.h2`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

const ModalContent = styled.div`
  padding: ${Spacing.xxxl}px;
  max-height: 60vh;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  padding: ${Spacing.xl}px ${Spacing.xxxl}px ${Spacing.xxxl}px;
  border-top: 1px solid ${Colors.border};
  background: ${Colors.backgroundSecondary};
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing.md}px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${Spacing.xl}px;
  right: ${Spacing.xl}px;
  background: none;
  border: none;
  font-size: ${FontSizes.xl}px;
  color: ${Colors.textTertiary};
  cursor: pointer;
  padding: ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  transition: all 200ms ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }
  
  &:focus {
    outline: none;
    background: ${Colors.surfaceSelected};
    color: ${Colors.primary};
  }
`;

const LogEntry = styled.div`
  padding: ${Spacing.md}px 0;
  border-bottom: 1px solid ${Colors.borderLight};
  
  &:last-child {
    border-bottom: none;
  }
`;

const LogTime = styled.span`
  font-family: ${FontStacks.mono};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-right: ${Spacing.md}px;
`;

const LogMessage = styled.span`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

const ConfigForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

const FormLabel = styled.label`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
`;

const FormInput = styled.input`
  padding: ${Spacing.md}px;
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  background: ${Colors.surface};
  color: ${Colors.textPrimary};
  transition: all 150ms ease;
  
  &:focus {
    outline: none;
    border-color: ${Colors.secondary};
    box-shadow: 0 0 0 3px ${Colors.secondary}20;
  }
  
  &::placeholder {
    color: ${Colors.textTertiary};
  }
`;

interface ModalState {
  type: 'config' | 'logs' | null;
  sourceId: string | null;
  sourceName: string | null;
}




const DataSourcesScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sources, syncInProgress, lastSyncTime, dataQualityAlerts } = useSelector(
    (state: RootState) => state.dataSources
  );
  const [modalState, setModalState] = useState<ModalState>({ type: null, sourceId: null, sourceName: null });
  const [refreshing, setRefreshing] = useState(false);
  const { notifications, addNotification, removeNotification } = useNotifications();

  useEffect(() => {
    initializeDataSources();
  }, []);

  const initializeDataSources = () => {
    const mockSources: DataSource[] = [
      {
        id: '1',
        name: 'Workday',
        type: 'WORKDAY',
        status: 'CONNECTED',
        lastSync: new Date(Date.now() - 3600000), // 1 hour ago
        recordsCount: 2847,
        dataQuality: 98,
      },
      {
        id: '2',
        name: 'SAP SuccessFactors',
        type: 'SAP',
        status: 'CONNECTED',
        lastSync: new Date(Date.now() - 7200000), // 2 hours ago
        recordsCount: 1523,
        dataQuality: 95,
      },
      {
        id: '3',
        name: 'Compensation Data',
        type: 'EXCEL',
        status: 'CONNECTED',
        lastSync: new Date(Date.now() - 86400000), // 1 day ago
        recordsCount: 2847,
        dataQuality: 92,
      },
      {
        id: '4',
        name: 'Performance Reviews',
        type: 'EXCEL',
        status: 'ERROR',
        lastSync: new Date(Date.now() - 172800000), // 2 days ago
        recordsCount: 2103,
        dataQuality: 78,
      },
    ];

    dispatch(setSources(mockSources));

    // Simulate data quality alert
    dispatch(addDataQualityAlert({
      id: '1',
      sourceId: '4',
      severity: 'HIGH',
      message: 'Missing performance data for 15% of employees',
      timestamp: new Date(),
    }));
  };

  const handleSync = (sourceId: string) => {
    const source = sources.find(s => s.id === sourceId);
    const sourceName = source?.name || 'Data source';
    
    dispatch(setSyncInProgress(true));
    dispatch(updateSourceStatus({ id: sourceId, status: 'SYNCING' }));
    AccessibilityUtils.announce('Starting synchronization');
    
    addNotification({
      type: 'info',
      title: 'Sync Started',
      message: `Synchronizing ${sourceName}...`,
      duration: 2000
    });

    setTimeout(() => {
      dispatch(updateSourceStatus({ id: sourceId, status: 'CONNECTED' }));
      dispatch(setSyncInProgress(false));
      AccessibilityUtils.announce('Data source synchronized successfully');
      
      addNotification({
        type: 'success',
        title: 'Sync Complete',
        message: `${sourceName} synchronized successfully`,
        duration: 3000
      });
    }, 3000);
  };

  const handleSyncAll = () => {
    setRefreshing(true);
    dispatch(setSyncInProgress(true));
    sources.forEach(source => {
      dispatch(updateSourceStatus({ id: source.id, status: 'SYNCING' }));
    });
    AccessibilityUtils.announce('Starting synchronization for all data sources');
    
    addNotification({
      type: 'info',
      title: 'Bulk Sync Started',
      message: `Synchronizing ${sources.length} data sources...`,
      duration: 2500
    });

    setTimeout(() => {
      let errorCount = 0;
      sources.forEach(source => {
        const isError = source.id === '4';
        if (isError) errorCount++;
        dispatch(updateSourceStatus({ 
          id: source.id, 
          status: isError ? 'ERROR' : 'CONNECTED' 
        }));
      });
      dispatch(setSyncInProgress(false));
      setRefreshing(false);
      AccessibilityUtils.announce('All data sources synchronized');
      
      if (errorCount > 0) {
        addNotification({
          type: 'warning',
          title: 'Sync Completed with Errors',
          message: `${sources.length - errorCount} sources synchronized, ${errorCount} failed`,
          duration: 4000
        });
      } else {
        addNotification({
          type: 'success',
          title: 'All Sources Synced',
          message: `Successfully synchronized ${sources.length} data sources`,
          duration: 3000
        });
      }
    }, 5000);
  };

  const handleConfigure = (sourceId: string, sourceName: string) => {
    setModalState({ type: 'config', sourceId, sourceName });
    AccessibilityUtils.announce(`Opening configuration for ${sourceName}`);
  };

  const handleViewLogs = (sourceId: string, sourceName: string) => {
    setModalState({ type: 'logs', sourceId, sourceName });
    AccessibilityUtils.announce(`Opening logs for ${sourceName}`);
  };

  const handleCloseModal = () => {
    setModalState({ type: null, sourceId: null, sourceName: null });
    AccessibilityUtils.announce('Modal closed');
  };

  const handleAlertClick = (alert: any) => {
    AccessibilityUtils.announce(`Viewing alert: ${alert.message}`);
    // Could open a detailed alert modal
    console.log('Alert clicked:', alert);
  };

  const getStatusIcon = (status: DataSource['status']) => {
    switch (status) {
      case 'CONNECTED': return '✓';
      case 'SYNCING': return '↻';
      case 'ERROR': return '⚠';
      case 'DISCONNECTED': return '○';
      default: return '❓';
    }
  };

  const getDataQualityColor = (quality?: number) => {
    if (!quality) return Colors.textTertiary;
    if (quality >= 95) return Colors.success;
    if (quality >= 85) return Colors.warning;
    return Colors.danger;
  };

  const formatLastSync = (date?: Date) => {
    if (!date) return t('dataSources.neverSynced');
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const mockLogs = [
    { time: '2025-01-15 14:30:25', message: 'Connection established successfully' },
    { time: '2025-01-15 14:30:22', message: 'Authenticating with data source' },
    { time: '2025-01-15 14:30:20', message: 'Initiating connection to data source' },
    { time: '2025-01-15 12:15:10', message: 'Previous sync completed with 2,847 records' },
    { time: '2025-01-15 12:15:05', message: 'Data validation completed successfully' },
  ];

  return (
    <ScreenContainer>
      <NotificationSystem 
        notifications={notifications}
        onDismiss={removeNotification}
      />
      <div className="container">
        <UnifiedPageHeader
          title={t('dataSources.title')}
          subtitle={t('dataSources.subtitle')}
          metadata={{
            timestamp: new Date(),
            count: sources.length,
            status: syncInProgress ? t('common.loading') : `${sources.filter(s => s.status === 'CONNECTED').length}/${sources.length} ${t('dataSources.status.connected')}`
          }}
          actions={[
            {
              label: t('dashboard.actions.refresh'),
              onClick: () => window.location.reload(),
              variant: 'ghost',
              icon: '🔄',
              ariaLabel: 'Refresh page'
            },
            {
              label: syncInProgress ? t('common.loading') : t('dataSources.actions.sync'),
              onClick: handleSyncAll,
              variant: 'primary',
              icon: '🔄',
              disabled: syncInProgress,
              loading: refreshing,
              ariaLabel: syncInProgress ? 'Syncing all sources' : 'Sync all sources'
            }
          ]}
        />

        {/* System Status - Moved to Top */}
        <SystemStatusSection>
          <SectionHeader>
            <SectionTitle>System Status</SectionTitle>
          </SectionHeader>
          <SystemStatusGrid>
            <StatusCard>
              <StatusValue color={sources.filter(s => s.status === 'CONNECTED').length === sources.length ? Colors.success : Colors.warning}>
                {sources.filter(s => s.status === 'CONNECTED').length}/{sources.length}
              </StatusValue>
              <StatusLabel>{t('dataSources.status.connected')} Sources</StatusLabel>
            </StatusCard>
            <StatusCard>
              <StatusValue>
                {sources.reduce((sum, s) => sum + (s.recordsCount || 0), 0).toLocaleString()}
              </StatusValue>
              <StatusLabel>Total Records</StatusLabel>
            </StatusCard>
            <StatusCard>
              <StatusValue color={getDataQualityColor(Math.round(sources.reduce((sum, s) => sum + (s.dataQuality || 0), 0) / sources.length))}>
                {Math.round(
                  sources.reduce((sum, s) => sum + (s.dataQuality || 0), 0) / sources.length
                )}%
              </StatusValue>
              <StatusLabel>Avg Data Quality</StatusLabel>
            </StatusCard>
            <StatusCard>
              <StatusValue color={dataQualityAlerts.length > 0 ? Colors.danger : Colors.success}>
                {dataQualityAlerts.length}
              </StatusValue>
              <StatusLabel>Active Alerts</StatusLabel>
            </StatusCard>
          </SystemStatusGrid>
        </SystemStatusSection>

        {/* Data Quality Alerts */}
        {dataQualityAlerts.length > 0 && (
          <AlertsContainer>
            <AlertsTitle>Data Quality Alerts</AlertsTitle>
            {dataQualityAlerts.map((alert) => (
              <AlertCard 
                key={alert.id} 
                severity={alert.severity}
                onClick={() => handleAlertClick(alert)}
                role="button"
                tabIndex={0}
                aria-label={`${alert.severity} severity alert: ${alert.message}`}
              >
                <AlertHeader>
                  <SeverityBadge severity={alert.severity}>
                    {alert.severity} priority
                  </SeverityBadge>
                  <AlertTime>
                    {formatLastSync(alert.timestamp)}
                  </AlertTime>
                </AlertHeader>
                <AlertMessage>{alert.message}</AlertMessage>
              </AlertCard>
            ))}
          </AlertsContainer>
        )}

        {/* Data Sources */}
        <SectionHeader>
          <SectionTitle>Data Source Integration</SectionTitle>
        </SectionHeader>
        
        <SourcesContainer>
          {sources.map((source) => (
            <SourceCard 
              key={source.id}
              role="button"
              tabIndex={0}
              aria-label={`${source.name} data source, status: ${source.status}`}
            >
              <SourceHeader>
                <SourceInfo>
                  <SourceName>{source.name}</SourceName>
                  <SourceType>{source.type}</SourceType>
                  <InlineSyncStatus 
                    status={
                      source.status === 'CONNECTED' ? 'active' :
                      source.status === 'SYNCING' ? 'syncing' :
                      source.status === 'ERROR' ? 'error' : 'offline'
                    }
                    lastSync={source.lastSync}
                  />
                </SourceInfo>
                <StatusBadge status={source.status}>
                  <StatusIcon>{getStatusIcon(source.status)}</StatusIcon>
                  {source.status === 'SYNCING' ? 'SYNCING...' : source.status}
                </StatusBadge>
              </SourceHeader>

              <SourceMetrics>
                <MetricItem>
                  <MetricLabel>Records</MetricLabel>
                  <MetricValue>
                    {source.recordsCount?.toLocaleString() || 'N/A'}
                  </MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>{t('dataSources.lastSync', { time: '' }).split(':')[0]}</MetricLabel>
                  <MetricValue>
                    {formatLastSync(source.lastSync)}
                  </MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Quality</MetricLabel>
                  <MetricValue color={getDataQualityColor(source.dataQuality)}>
                    {source.dataQuality ? `${source.dataQuality}%` : 'N/A'}
                  </MetricValue>
                </MetricItem>
              </SourceMetrics>

              {source.dataQuality && (
                <QualityBar>
                  <QualityProgress
                    width={source.dataQuality}
                    color={getDataQualityColor(source.dataQuality)}
                  />
                </QualityBar>
              )}

              <SourceActions>
                <ActionButton
                  variant="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSync(source.id);
                  }}
                  disabled={source.status === 'SYNCING' || syncInProgress}
                  aria-label={`${source.status === 'SYNCING' ? 'Syncing' : 'Sync'} ${source.name}`}
                >
                  {source.status === 'SYNCING' ? t('common.loading') : t('dataSources.actions.sync')}
                </ActionButton>
                
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfigure(source.id, source.name);
                  }}
                  aria-label={`Configure ${source.name}`}
                >
                  {t('dataSources.actions.configure')}
                </ActionButton>
                
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewLogs(source.id, source.name);
                  }}
                  aria-label={`View logs for ${source.name}`}
                >
                  View Logs
                </ActionButton>
              </SourceActions>
            </SourceCard>
          ))}
        </SourcesContainer>

        {/* Modals */}
        {modalState.type === 'config' && (
          <ModalOverlay 
            isOpen={true} 
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseModal();
            }}
          >
            <ModalContainer isOpen={true}>
              <CloseButton onClick={handleCloseModal} aria-label="Close configuration">
                ×
              </CloseButton>
              
              <ModalHeader>
                <ModalTitle>{t('dataSources.actions.configure')} {modalState.sourceName}</ModalTitle>
              </ModalHeader>

              <ModalContent>
                <ConfigForm>
                  <FormGroup>
                    <FormLabel>Connection URL</FormLabel>
                    <FormInput 
                      type="text" 
                      placeholder="https://api.example.com"
                      defaultValue="https://api.workday.com/v1"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>API Key</FormLabel>
                    <FormInput 
                      type="password" 
                      placeholder="Enter API key"
                      defaultValue="••••••••••••••••"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Sync Frequency</FormLabel>
                    <FormInput 
                      type="select" 
                      as="select"
                      defaultValue="hourly"
                    >
                      <option value="realtime">Real-time</option>
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </FormInput>
                  </FormGroup>
                </ConfigForm>
              </ModalContent>

              <ModalFooter>
                <InteractiveButton variant="secondary" size="md" onClick={handleCloseModal}>
                  Cancel
                </InteractiveButton>
                <InteractiveButton variant="primary" size="md">
                  Save Configuration
                </InteractiveButton>
              </ModalFooter>
            </ModalContainer>
          </ModalOverlay>
        )}

        {modalState.type === 'logs' && (
          <ModalOverlay 
            isOpen={true} 
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseModal();
            }}
          >
            <ModalContainer isOpen={true}>
              <CloseButton onClick={handleCloseModal} aria-label="Close logs">
                ×
              </CloseButton>
              
              <ModalHeader>
                <ModalTitle>Logs for {modalState.sourceName}</ModalTitle>
              </ModalHeader>

              <ModalContent>
                {mockLogs.map((log, index) => (
                  <LogEntry key={index}>
                    <LogTime>{log.time}</LogTime>
                    <LogMessage>{log.message}</LogMessage>
                  </LogEntry>
                ))}
              </ModalContent>

              <ModalFooter>
                <InteractiveButton variant="secondary" size="md" onClick={handleCloseModal}>
                  Close
                </InteractiveButton>
                <InteractiveButton variant="primary" size="md">
                  Download Logs
                </InteractiveButton>
              </ModalFooter>
            </ModalContainer>
          </ModalOverlay>
        )}
      </div>
    </ScreenContainer>
  );
};

export default DataSourcesScreen;